import { IHooksOptions } from '@/hooks/interface'
import service from '@/utils/request'
import { onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import qs from 'qs'
import { useI18n } from 'vue-i18n'

export const useCrud = (options: IHooksOptions) => {
	const { t } = useI18n()
	const defaultOptions: IHooksOptions = {
		createdIsNeed: true,
		dataListUrl: '',
		isPage: true,
		deleteUrl: '',
		primaryKey: 'id',
		exportUrl: '',
		queryForm: {},
		dataList: [],
		order: '',
		asc: false,
		page: 1,
		limit: 40,
		total: 0,
		pageSizes: [10, 20, 50, 100, 200, 500, 1000],
		dataListLoading: false,
		dataListSelections: [],
		dataListSelectionKeys: []
	}

	const mergeDefaultOptions = (options: any, props: any): IHooksOptions => {
		for (const key in options) {
			if (!Object.getOwnPropertyDescriptor(props, key)) {
				props[key] = options[key]
			}
		}
		return props
	}

	// 覆盖默认值
	const state = mergeDefaultOptions(defaultOptions, options)

	type ButtonStatus = 'PENDING' | 'DOING' | 'DONE' | 'CANCEL'
	interface CurrentValue {
		status: ButtonStatus
		[key: string]: any // 允许有其他不确定的键
	}
	const currentValue = ref<any>({ id: '', status: 'PENDING' })

	onMounted(() => {
		if (state.createdIsNeed) {
			return query()
		}
	})

	const query = () => {
		if (!state.dataListUrl) {
			return
		}
		state.dataListLoading = true
		if (!state.dataListMethods) {
			state.dataListMethods = 'get'
		}
		let data = {
			order: state.order,
			asc: state.asc,
			page: state.isPage ? state.page : null,
			limit: state.isPage ? state.limit : null,
			...state.queryForm
		}
		if ('dataFrom' in state) {
			data = state.dataFrom(data)
		}
		return service({
			method: state.dataListMethods,
			url: state.dataListUrl,
			params: state.dataListMethods == 'get' ? data : undefined,
			data: state.dataListMethods != 'GET' ? data : undefined
		})
			.then((res: any) => {
				state.dataList = state.isPage ? res.data.list : res.data
				if ('dataTo' in state) {
					state.dataList = state.dataTo(state.dataList)
				}
				state.total = state.isPage ? res.data.total : 0
				//默认
				if (state.dataList && state.dataList.length && state.dataListUrl === '/maku/biz_workorder/page') {
					currentValue.value = state.dataList[0]
				}
			})
			.finally(() => {
				state.dataListLoading = false
			})
	}

	const getDataList = (val: number = 1) => {
		// state.page = val
		query()
	}

	const sizeChangeHandle = (val: number) => {
		state.page = 1
		state.limit = val
		query()
	}

	const currentChangeHandle = (val: number) => {
		state.page = val
		query()
	}

	// 多选
	const selectionChangeHandle = (selections: any[]) => {
		state.dataListSelections = selections
		state.dataListSelectionKeys = selections.map((item: any) => state.primaryKey && item[state.primaryKey])
	}

	// 排序
	const sortChangeHandle = (data: any) => {
		const { prop, order } = data

		if (prop && order) {
			state.order = prop
			state.asc = order === 'ascending'
		} else {
			state.order = ''
		}

		query()
	}

	const deleteHandle = (key: number | string) => {
		if (!state.deleteUrl) {
			return
		}

		ElMessageBox.confirm(t('componentsModule.hooks.deleteConfirm'), t('common.action'), {
			confirmButtonText: t('common.confirm'),
			cancelButtonText: t('common.cancel'),
			type: 'warning'
		})
			.then(() => {
				service.delete(state.deleteUrl + '/' + key).then(() => {
					ElMessage.success(t('componentsModule.hooks.deleteSuccess'))
					query()
				})
			})
			.catch(() => {})
	}

	const deleteBatchHandle = (key?: number | string | any[]) => {
		let data: any[] = []
		if (key && (typeof key === 'number' || typeof key === 'string')) {
			data = [key]
		} else if (Array.isArray(key)) {
			data = key
		} else {
			data = state.dataListSelectionKeys ? state.dataListSelectionKeys : []
			if (data.length === 0) {
				ElMessage.warning(t('componentsModule.hooks.selectDeleteWarning'))
				return
			}
		}

		ElMessageBox.confirm(t('componentsModule.hooks.deleteConfirm'), t('componentsModule.hooks.action'), {
			confirmButtonText: t('componentsModule.hooks.confirm'),
			cancelButtonText: t('componentsModule.hooks.cancel'),
			type: 'warning'
		})
			.then(() => {
				if (state.deleteUrl) {
					service.delete(state.deleteUrl, { data }).then(() => {
						ElMessage.success(t('componentsModule.hooks.deleteSuccess'))

						query()
					})
				}
			})
			.catch(() => {})
	}

	const downloadHandle = async (url: string, filename?: string, method: string = 'GET', data?: any): Promise<any> => {
		// 对URL中的#号进行编码处理
		const encodedUrl = url.replace(/#/g, '%23')
		try {
			const res = await service({
				responseType: 'blob',
				url: encodedUrl,
				method: method,
				params: method == 'GET' ? data : undefined,
				data: method != 'GET' ? data : undefined
			})
			// 创建a标签
			const down = document.createElement('a')

			// 文件名没传，则使用时间戳
			if (filename) {
				down.download = filename
			} else {
				const downName = res.headers['content-disposition'].split('=')[1]
				down.download = decodeURI(downName)
			}

			// 隐藏a标签
			down.style.display = 'none'

			// 创建下载url
			down.href = URL.createObjectURL(
				new Blob([res.data], {
					type: res.data.type
				})
			)

			// 模拟点击下载
			document.body.appendChild(down)
			down.click()

			// 释放URL
			URL.revokeObjectURL(down.href)
			// 下载完成移除
			document.body.removeChild(down)
			ElMessage.success(t('componentsModule.hooks.downloadSuccess'))
		} catch (err: any) {
			// ElMessage.error(err.message)
		}
	}

	const reset = (queryRef: any) => {
		queryRef.resetFields()
		state.queryForm = {}
		getDataList()
	}

	return {
		getDataList,
		sizeChangeHandle,
		currentChangeHandle,
		selectionChangeHandle,
		sortChangeHandle,
		deleteHandle,
		deleteBatchHandle,
		downloadHandle,
		reset,
		currentValue
	}
}
